import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { getEvents, getRegistrations, updateRegistration } from "../api/api";
import { useNotifications } from "../components/NotificationsContext";

const STATUS_OPTIONS = [
  { value: "en_attente", label: "En attente" },
  { value: "active", label: "Active" },
  { value: "terminee", label: "Terminee" },
  { value: "annulee", label: "Annulee" },
];

function money(value) {
  return `${Number(value || 0).toFixed(2)} DT`;
}

function csvCell(value) {
  return `"${String(value ?? "").replaceAll('"', '""')}"`;
}

function downloadTextFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function statusBadge(status) {
  if (status === "active") return <Badge bg="success">Active</Badge>;
  if (status === "en_attente") return <Badge bg="warning" text="dark">En attente</Badge>;
  if (status === "terminee") return <Badge bg="secondary">Terminee</Badge>;
  if (status === "annulee") return <Badge bg="danger">Annulee</Badge>;
  return <Badge bg="light" text="dark">-</Badge>;
}

function isTodayOrFuture(value) {
  if (!value) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(value) >= today;
}

function personDetailsText(details) {
  if (!details) return "";
  const parts = [
    details.cin ? `CIN ${details.cin}` : null,
    details.date_naissance ? `Ne(e) le ${details.date_naissance}` : null,
    details.age !== undefined && details.age !== null ? `${details.age} ans` : null,
    details.sexe ? `Sexe ${details.sexe}` : null,
    details.classe ? `Classe ${details.classe}` : null,
    details.etablissement,
    details.situation ? `Situation ${details.situation}` : null,
  ].filter(Boolean);
  return parts.join(" | ");
}

export default function RegistrationsAdmin({ embedded = false }) {
  const { addNotification } = useNotifications();

  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const [eventFilter, setEventFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [evRes, regRes] = await Promise.all([getEvents(), getRegistrations()]);
        setEvents(Array.isArray(evRes.data) ? evRes.data.filter((event) => isTodayOrFuture(event.date_evenement || event.date_debut)) : []);
        setRegistrations(Array.isArray(regRes.data) ? regRes.data.filter((registration) => isTodayOrFuture(registration.event_date_evenement || registration.event_date_debut)) : []);
      } catch (err) {
        addNotification({
          type: "error",
          title: "Erreur",
          message: "Impossible de charger les inscriptions.",
          delay: 7000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [addNotification]);

  const filtered = useMemo(() => {
    return registrations.filter((registration) => {
      if (eventFilter && String(registration.event) !== String(eventFilter)) return false;
      if (statusFilter && registration.statut !== statusFilter) return false;
      return true;
    });
  }, [registrations, eventFilter, statusFilter]);

  const groups = useMemo(() => {
    const map = new Map();

    filtered.forEach((registration) => {
      const key = [
        registration.event,
        registration.adherent,
        registration.tranche,
      ].join("_");

      if (!map.has(key)) {
        map.set(key, {
          key,
          event: registration.event,
          eventTitle: registration.event_titre || `Evenement #${registration.event}`,
          eventDate: registration.event_date_evenement || registration.event_date_debut,
          adherent: registration.adherent,
          adherentName: registration.adherent_full_name || registration.adherent_matricule || registration.adherent,
          adherentMatricule: registration.adherent_matricule || registration.adherent,
          adherentCin: registration.adherent_cin || "",
          tranche: registration.tranche,
          date: registration.date_inscription,
          participants: [],
        });
      }

      map.get(key).participants.push(registration);
    });

    return Array.from(map.values()).map((group) => {
      const totalNet = group.participants.reduce((sum, item) => sum + Number(item.montant_net || 0), 0);
      const totalQuota = group.participants.reduce((sum, item) => sum + Number(item.montant_quota ?? item.montant_net ?? 0), 0);
      const totalMonthly = group.participants.reduce((sum, item) => sum + Number(item.montant_mensuel || 0), 0);
      return { ...group, totalNet, totalQuota, totalMonthly };
    });
  }, [filtered]);

  const updateParticipantStatus = async (registrationId, nextStatus) => {
    setUpdatingId(registrationId);
    try {
      await updateRegistration(registrationId, { statut: nextStatus });
      setRegistrations((prev) =>
        prev.map((item) =>
          item.id === registrationId ? { ...item, statut: nextStatus } : item
        )
      );
      addNotification({
        type: "success",
        title: "Statut modifie",
        message: "Statut de l'inscription mis a jour.",
        delay: 3000,
      });
    } catch (err) {
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Impossible de modifier le statut.",
        delay: 6000,
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const exportExcel = () => {
    const rows = [
      ["Evenement", "Adherent", "Matricule", "Duree paiement", "Participants", "Montant net total", "Impact quota", "Mensuel total", "Date"],
      ...groups.map((group) => [
        group.eventTitle,
        group.adherentName,
        group.adherentMatricule,
        `${group.tranche} mois`,
        group.participants
          .map((item) => `${item.participant_type || "-"}: ${item.participant_label || "-"} (${item.statut})`)
          .join(" | "),
        group.totalNet.toFixed(2),
        group.totalQuota.toFixed(2),
        group.totalMonthly.toFixed(2),
        group.date ? String(group.date).slice(0, 16) : "",
      ]),
    ];

    const csv = rows.map((row) => row.map(csvCell).join(";")).join("\n");
    downloadTextFile("inscriptions-evenements.csv", csv, "text/csv;charset=utf-8");
  };

  const exportPdf = () => {
    const rows = groups.map((group) => `
      <tr>
        <td>${group.eventTitle}</td>
        <td>${group.adherentName}<br/><small>${group.adherentMatricule}</small></td>
        <td>${group.tranche} mois</td>
        <td>${group.participants.map((item) => `${item.participant_type || "-"}: ${item.participant_label || "-"} (${item.statut})`).join("<br/>")}</td>
        <td>${money(group.totalNet)}</td>
        <td>${money(group.totalQuota)}</td>
        <td>${money(group.totalMonthly)}</td>
        <td>${group.date ? String(group.date).slice(0, 16) : ""}</td>
      </tr>
    `).join("");

    const win = window.open("", "_blank");
    if (!win) return;

    win.document.write(`
      <html>
        <head>
          <title>Inscriptions evenements</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; vertical-align: top; }
            th { background: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Inscriptions evenements</h2>
          <table>
            <thead>
              <tr>
                <th>Evenement</th><th>Adherent</th><th>Duree paiement</th><th>Participants</th>
                <th>Montant net total</th><th>Impact quota</th><th>Mensuel total</th><th>Date</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <div className={embedded ? "pt-2" : "p-4"}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          {!embedded && <h4 className="mb-1">Inscriptions evenements</h4>}
          <div className="text-muted">
            Une ligne par adherent, evenement et duree de paiement. Les membres de famille sont regroupes dans la meme ligne.
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-success" onClick={exportExcel} disabled={groups.length === 0}>
            Export Excel
          </Button>
          <Button variant="outline-danger" onClick={exportPdf} disabled={groups.length === 0}>
            Export PDF
          </Button>
        </div>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col md={6}>
              <Form.Label>Filtrer par evenement</Form.Label>
              <Form.Select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}>
                <option value="">Tous</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.titre}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={6}>
              <Form.Label>Filtrer par statut</Form.Label>
              <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">Tous</option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <Card.Body className="text-center text-muted py-5">
            Aucune inscription trouvee.
          </Card.Body>
        </Card>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Evenement</th>
              <th>Adherent payeur</th>
              <th>Participants</th>
              <th>Duree paiement</th>
              <th>Montant total</th>
              <th>Quota</th>
              <th>Mensuel total</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.key}>
                <td>{group.eventTitle}</td>
                <td>
                  <strong>{group.adherentName}</strong>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                  {group.adherentMatricule}
                    {group.adherentCin ? ` | CIN ${group.adherentCin}` : ""}
                  </div>
                </td>
                <td style={{ minWidth: 360 }}>
                  {group.participants.map((participant) => (
                    <div key={participant.id} className="d-flex align-items-center justify-content-between gap-2 border rounded-3 p-2 mb-2 bg-white">
                      <div>
                        <div style={{ fontWeight: 700 }}>
                          {participant.participant_type || "Participant"}: {participant.participant_label || "-"}
                        </div>
                        <div>{statusBadge(participant.statut)}</div>
                        {participant.participant_details && (
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {personDetailsText(participant.participant_details)}
                          </div>
                        )}
                      </div>
                      <Form.Select
                        size="sm"
                        style={{ maxWidth: 150 }}
                        value={participant.statut}
                        disabled={updatingId === participant.id}
                        onChange={(e) => updateParticipantStatus(participant.id, e.target.value)}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  ))}
                </td>
                <td>{group.tranche} mois</td>
                <td>{money(group.totalNet)}</td>
                <td>{money(group.totalQuota)}</td>
                <td>{money(group.totalMonthly)}</td>
                <td>{group.date ? String(group.date).slice(0, 16) : "-"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
